import { useEffect, useState } from 'react'
import { getLocations, getUsers, getVendors } from '../../api/master'
import AccountSelect, { costAccount } from '../../components/AccountSelect'
import { HelpField } from '../../components/HelpTip'
import { money, num } from '../sales/salesShared'
import { CreateOption } from '../../components/CreateOption'

export const CAPITALIZATION_SOURCES = [
  ['Cash', 'Paid in cash'], ['AP', 'On account (payables)'], ['VendorInvoice', 'Through a vendor invoice'],
  ['OpeningBalance', 'Opening balance'], ['Other', 'Other'],
]

/** An asset as form values. */
export const assetToForm = (a = {}) => ({
  name: a.name || '',
  category: a.category || '',
  acquisition_date: String(a.acquisition_date || '').slice(0, 10),
  location_id: a.location_id ? String(a.location_id) : '',
  cost: a.cost != null ? String(a.cost) : '',
  salvage_value: a.salvage_value != null ? String(a.salvage_value) : '0',
  useful_life_months: a.useful_life_months ? String(a.useful_life_months) : '',
  status: a.status || 'Active',
  asset_account_code: a.asset_account_code || '',
  accum_dep_account_code: a.accum_dep_account_code || '',
  expense_account_code: a.expense_account_code || '',
  vendor_id: a.vendor_id ? String(a.vendor_id) : '',
  custodian_user_id: a.custodian_user_id ? String(a.custodian_user_id) : '',
  department: a.department || '',
  serial_number: a.serial_number || '',
  model: a.model || '',
  manufacturer: a.manufacturer || '',
  capitalization_source: a.capitalization_source || 'Other',
})

/** Field problems, or {} when the form can be saved. */
export function checkAsset(f, { periodsPosted = 0, locked = false } = {}) {
  const e = {}
  if (!f.name.trim()) e.name = 'Give the asset a name.'
  const cost = num(f.cost)
  const salv = num(f.salvage_value)
  const life = parseInt(f.useful_life_months, 10)
  if (!locked && !(cost > 0)) e.cost = 'Enter what it cost.'
  if (salv < 0) e.salvage_value = 'Can’t be negative.'
  else if (salv > cost) e.salvage_value = 'Can’t be more than the cost.'
  if (!(life > 0)) e.useful_life_months = 'Enter how many months it will be used.'
  else if (life <= periodsPosted) e.useful_life_months = `Must be more than the ${periodsPosted} months already posted.`
  if (!locked) {
    if (!f.asset_account_code) e.asset_account_code = 'Choose the asset account.'
    if (!f.accum_dep_account_code) e.accum_dep_account_code = 'Choose the accumulated depreciation account.'
    if (!f.expense_account_code) e.expense_account_code = 'Choose the depreciation expense account.'
  }
  return e
}

const Err = ({ msg }) => (msg ? <span className="acc-field-error">{msg}</span> : null)

// The asset's fields, shared by "Add a fixed asset" and the asset's Edit.
// `locked` keeps cost and accounts as they are once depreciation is posted.
export default function AssetFields({ form, set, errors = {}, locked = false, editing = false }) {
  const [locations, setLocations] = useState([])
  const [vendors, setVendors] = useState([])
  const [users, setUsers] = useState([])
  useEffect(() => {
    getLocations().then((l) => setLocations(l || [])).catch(() => {})
    getVendors().then((v) => setVendors(v || [])).catch(() => {})
    // Not a practice's accountants: they don't look after the company's things.
    getUsers().then((u) => setUsers((u || []).filter((x) => !x.practice_id))).catch(() => {})
  }, [])
  const field = (key) => ({ value: form[key], onChange: (e) => set({ [key]: e.target.value }) })
  const cost = num(form.cost)
  const life = parseInt(form.useful_life_months, 10)
  const monthly = life > 0 && cost > 0 ? (cost - num(form.salvage_value)) / life : 0

  return (
    <>
      <div className="ci-form-meta-card acc-stack">
        <div className="ci-form-meta-top two">
          <label className="ci-form-field">
            <span>Name</span>
            <input {...field('name')} placeholder="e.g. Delivery van" />
            <Err msg={errors.name} />
          </label>
          <label className="ci-form-field">
            <span>Category (optional)</span>
            <input {...field('category')} placeholder="e.g. Vehicles" />
          </label>
        </div>
        <div className="ci-form-meta-top three">
          <label className="ci-form-field">
            <span>Acquired on</span>
            <input type="date" {...field('acquisition_date')} disabled={editing} />
          </label>
          <label className="ci-form-field">
            <span>Location (optional)</span>
            <select {...field('location_id')}>
              <option value="">None</option>
              <CreateOption kind="location" />
              {locations.map((l) => <option key={l.location_id} value={l.location_id}>{l.name}</option>)}
            </select>
          </label>
          {editing ? (
            <label className="ci-form-field">
              <span>Status</span>
              <select {...field('status')}>
                <option value="Active">Active</option>
                <option value="Paused">Paused (no depreciation)</option>
              </select>
            </label>
          ) : <div />}
        </div>
      </div>

      <div className="ci-form-meta-card">
        <div className="acc-card-title">Cost and depreciation</div>
        <div className="ci-form-meta-top three">
          <label className="ci-form-field">
            <span>Cost</span>
            <input {...field('cost')} inputMode="decimal" placeholder="0.00" disabled={locked} />
            <Err msg={errors.cost} />
          </label>
          <HelpField label="Residual value" k="residualValue" error={errors.salvage_value}>
            <input {...field('salvage_value')} inputMode="decimal" placeholder="0.00" />
          </HelpField>
          <HelpField label="Useful life (months)" k="usefulLife" error={errors.useful_life_months}>
            <input {...field('useful_life_months')} inputMode="numeric" placeholder="e.g. 60" />
          </HelpField>
        </div>
        {monthly > 0 && <p className="doc-hint acc-monthly">Depreciation: {money(monthly)} a month, straight-line.</p>}
        {locked && <p className="doc-hint acc-monthly">Cost and accounts stay as they are once depreciation is posted. A new residual value or useful life applies from the next month.</p>}
      </div>

      <div className="ci-form-meta-card">
        <div className="acc-card-title">Accounts</div>
        <div className="ci-form-meta-top three">
          <HelpField label="Asset account" k="assetAccount" error={errors.asset_account_code}>
            <AccountSelect value={form.asset_account_code} onChange={(v) => set({ asset_account_code: v })} types={['Asset']} disabled={locked} />
          </HelpField>
          <HelpField label="Accumulated depreciation" k="accumDepAccount" error={errors.accum_dep_account_code}>
            <AccountSelect value={form.accum_dep_account_code} onChange={(v) => set({ accum_dep_account_code: v })} types={['Asset']} disabled={locked} />
          </HelpField>
          <HelpField label="Depreciation expense" k="depExpenseAccount" error={errors.expense_account_code}>
            <AccountSelect value={form.expense_account_code} onChange={(v) => set({ expense_account_code: v })} types={['Expense', 'Asset']} filter={costAccount} disabled={locked} />
          </HelpField>
        </div>
      </div>

      <details className="ci-form-meta-card acc-more">
        <summary>More details (optional)</summary>
        <div className="ci-form-meta-top three">
          <label className="ci-form-field">
            <span>Bought from</span>
            <select {...field('vendor_id')}>
              <option value="">None</option>
              <CreateOption kind="vendor" />
              {vendors.map((v) => <option key={v.vendor_id} value={v.vendor_id}>{v.name}</option>)}
            </select>
          </label>
          <label className="ci-form-field">
            <span>Looked after by</span>
            <select {...field('custodian_user_id')}>
              <option value="">No one</option>
              {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.full_name || u.username}</option>)}
            </select>
          </label>
          <label className="ci-form-field">
            <span>Department</span>
            <input {...field('department')} placeholder="e.g. Warehouse" />
          </label>
          <label className="ci-form-field">
            <span>Serial number</span>
            <input {...field('serial_number')} />
          </label>
          <label className="ci-form-field">
            <span>Model</span>
            <input {...field('model')} />
          </label>
          <label className="ci-form-field">
            <span>Manufacturer</span>
            <input {...field('manufacturer')} />
          </label>
          <label className="ci-form-field">
            <span>How it was paid for</span>
            <select {...field('capitalization_source')}>
              {CAPITALIZATION_SOURCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
        </div>
      </details>
    </>
  )
}
